import { useCallback, useEffect, useRef, useState } from 'react';
import { getCaptcha } from '../api/captcha';
import {
  extractCaptchaError,
  extractPaymentErrorMessage,
  isCaptchaErrorMessage,
  shouldReloadCaptcha,
} from '../utils/payment-v5-response';

export {
  extractCaptchaError,
  extractPaymentErrorMessage,
  isCaptchaErrorMessage,
  shouldReloadCaptcha,
};

/**
 * Carrega e gerencia o CAPTCHA de imagem (Laravel GET /api/captcha).
 * Expõe expiresAt / secondsLeft com base em expires_in (padrão 300s).
 * Ao expirar, regenera automaticamente (se autoReloadOnExpire).
 */
export function useCaptcha({ autoLoad = true, autoReloadOnExpire = true } = {}) {
  const [captcha, setCaptcha] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const autoReloadOnExpireRef = useRef(autoReloadOnExpire);

  useEffect(() => {
    autoReloadOnExpireRef.current = autoReloadOnExpire;
  }, [autoReloadOnExpire]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadCaptcha = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const data = await getCaptcha();

      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return null;
      }

      const expiresIn =
        typeof data.expires_in === 'number' && data.expires_in > 0
          ? data.expires_in
          : 300;

      setCaptcha(data);
      setExpiresAt(Date.now() + expiresIn * 1000);
      setSecondsLeft(expiresIn);
      setLoading(false);
      return data;
    } catch (err) {
      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return null;
      }

      setCaptcha(null);
      setExpiresAt(null);
      setSecondsLeft(null);
      setError(
        err?.message ||
          'Não foi possível carregar o código de verificação. Tente novamente.'
      );
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadCaptcha();
    }
  }, [autoLoad, loadCaptcha]);

  // Countdown + auto-reload ao expirar (uma vez por ciclo de expiresAt)
  useEffect(() => {
    if (!expiresAt) {
      setSecondsLeft(null);
      return undefined;
    }

    let expiredHandled = false;

    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(left);

      if (left <= 0 && autoReloadOnExpireRef.current && !expiredHandled) {
        expiredHandled = true;
        loadCaptcha();
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt, loadCaptcha]);

  const clearCaptcha = useCallback(() => {
    setCaptcha(null);
    setExpiresAt(null);
    setSecondsLeft(null);
    setError(null);
  }, []);

  return {
    captcha,
    loading,
    error,
    captchaLoading: loading,
    captchaLoadError: error,
    expiresAt,
    secondsLeft,
    loadCaptcha,
    clearCaptcha,
    captchaId: captcha?.captcha_id || null,
    captchaImage: captcha?.image || null,
    hasValidCaptcha: Boolean(captcha?.captcha_id && captcha?.image),
    isExpired: secondsLeft !== null && secondsLeft <= 0,
  };
}

export default useCaptcha;
